/**
 * pages/high_risk/ashuaDaily/index.js
 * АШУА-ийн ХАБЭА ӨДӨР ТУТМЫН ТАЙЛАН — бүртгэл.
 *
 * Зам: /highrisk/ashuadaily
 *
 * Эх сурвалж: «ХАБЭА-н нэгдсэн систем → Анхан шатны удирдах ажилтны тайлан»
 *             (Screenshot 2026-08-12) — 14 асуумж.
 *
 * ГОЛ САНАА:
 *   Эх системд АШУА 14 тоог ГАРААР бөглөдөг ба практикт 3-14-р багана
 *   бараг бүгд 0 үлддэг. Гэтэл манай баазад тэр өдрүүдэд бодит бүртгэл бий.
 *
 *   Тиймээс энд:
 *     • «Автомат» үзүүлэлтийг манай бүртгэлээс тооцоод ЗӨВХӨН ХАРУУЛНА
 *     • АШУА-д зөвхөн ГАРААР оруулах хэсгийг л ил гаргана
 *
 *   Ингэснээр АШУА 14-ийн оронд 4-5 талбар бөглөх ба тайлан нь бодит
 *   бүртгэлтэй уялдана.
 *
 * Асуумжийн лавлах: /highrisk/reference → «Өдөр тутмын асуумж» таб.
 * Backend: /xab/ashua-daily*
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  message as antMessage,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import moment from "moment";

import { xabAshuaDailyApi } from "src/api/xabApi";
import DepartmentTseh from "src/components/departmentTseh";
import { SearchInput } from "src/components/filters";
import PageHelp from "src/components/help/PageHelp";
import "src/components/compactAlert.css";

const PAGE_BG = "#F1F3F6";
const MUTED = "#8A94A6";
const BORDER = "#E6E9EF";
const BRAND = "#0F6E56";

const filterLbl = {
  fontSize: 11,
  fontWeight: 700,
  color: "#5F5E5A",
  marginBottom: 3,
};

const MODE = {
  full: { label: "Автомат", color: "#16A34A", icon: "fa-bolt" },
  partial: { label: "Хэсэгчлэн", color: "#CA8A04", icon: "fa-adjust" },
  none: { label: "Гараар", color: "#DC2626", icon: "fa-pen" },
};

function KpiCard({ label, value, icon, color, hint }) {
  return (
    <div
      style={{
        flex: "1 1 130px",
        background: "#fff",
        border: "1px solid " + BORDER,
        borderRadius: 10,
        padding: "8px 10px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.3,
            color: MUTED,
            textTransform: "uppercase",
          }}
        >
          {label}
        </span>
        <i className={"fas " + icon} style={{ color, fontSize: 13 }} />
      </div>
      <div
        style={{
          fontSize: 19,
          fontWeight: 800,
          lineHeight: 1.15,
          marginTop: 3,
          color: "#1F2430",
        }}
      >
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 10, color: MUTED, marginTop: 1 }}>{hint}</div>
      )}
    </div>
  );
}

export default function AshuaDailyRegister() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], summary: {} });

  // Анхдагч — сүүлийн 1 сар (бусад хуудастай нэг зарчим)
  const [range, setRange] = useState([moment().subtract(1, "month"), moment()]);
  const [bn, setBn] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  // Харагдац: list — тайлан тус бүрээр, month — сарын матриц
  const [view, setView] = useState("list");
  const [formOpen, setFormOpen] = useState(false);
  const [formDate, setFormDate] = useState(moment());
  // Хэний тайланг нээж байна — хоосон бол нэвтэрсэн хэрэглэгч өөрөө.
  // Цехийн ХАБ хамрагдалтын хэсгээс өөр АШУА-гийн тайланг нээнэ.
  const [formTn, setFormTn] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    xabAshuaDailyApi
      .list({
        from: range?.[0]?.format("YYYY-MM-DD"),
        to: range?.[1]?.format("YYYY-MM-DD"),
        bn_code: bn || undefined,
        status: status || undefined,
      })
      .then((r) => setData(r || { items: [], summary: {} }))
      .catch((e) =>
        antMessage.error(e?.response?.data?.message || "Уншиж чадсангүй"),
      )
      .finally(() => setLoading(false));
  }, [range, bn, status]);

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(() => {
    const kw = q.trim().toLowerCase();
    if (!kw) return data.items || [];
    return (data.items || []).filter((x) =>
      [x.ashua_name, x.ashua_tn, x.dept_name, x.section_name]
        .map((v) => String(v || ""))
        .join(" ")
        .toLowerCase()
        .includes(kw),
    );
  }, [data.items, q]);

  const s = data.summary || {};

  const columns = [
    {
      title: "Огноо",
      dataIndex: "report_date",
      width: 100,
      render: (v) => <b style={{ fontSize: 12 }}>{v}</b>,
    },
    {
      title: "АШУА",
      dataIndex: "ashua_name",
      width: 180,
      render: (v, r) => (
        <div>
          <div style={{ fontSize: 12.5 }}>{v || "—"}</div>
          <div style={{ fontSize: 10.5, color: MUTED }}>
            {r.ashua_position || r.ashua_tn}
          </div>
        </div>
      ),
    },
    {
      title: "Бүтцийн нэгж",
      dataIndex: "dept_name",
      render: (v, r) => (
        <div>
          <div style={{ fontSize: 12 }}>{v || "—"}</div>
          {r.section_name && (
            <div style={{ fontSize: 10.5, color: MUTED }}>{r.section_name}</div>
          )}
        </div>
      ),
    },
    {
      title: "Нийт тоо",
      dataIndex: "total",
      width: 84,
      align: "right",
      render: (v) => <b>{v ?? 0}</b>,
    },
    {
      title: "Зөрүү",
      dataIndex: "mismatch",
      width: 80,
      align: "center",
      render: (v) =>
        v ? (
          <Tooltip title="Манай систем тооцсоноос АШУА өөр тоо бичсэн үзүүлэлтийн тоо">
            <Tag color="orange" style={{ marginRight: 0 }}>
              {v}
            </Tag>
          </Tooltip>
        ) : (
          <span style={{ color: MUTED }}>—</span>
        ),
    },
    {
      title: "Төлөв",
      dataIndex: "status",
      width: 110,
      render: (v) =>
        v === "submitted" ? (
          <Tag color="green" style={{ marginRight: 0 }}>
            Илгээсэн
          </Tag>
        ) : (
          <Tag style={{ marginRight: 0 }}>Ноорог</Tag>
        ),
    },
    {
      title: "",
      width: 132,
      render: (_, r) => (
        <div style={{ display: "flex", gap: 4 }}>
          <Tooltip title={r.can_edit ? "Нээх / засах" : "Зөвхөн харах"}>
            <Button
              size="small"
              icon={
                <i
                  className={"fas " + (r.can_edit ? "fa-pen" : "fa-eye")}
                  style={{ fontSize: 11 }}
                />
              }
              onClick={() => {
                setFormDate(moment(r.report_date));
                setFormTn(r.ashua_tn);
                setFormOpen(true);
              }}
            />
          </Tooltip>

          {/* Устгах — ЗӨВХӨН тайлан гаргасан хүн эсвэл ХАБЭА хэлтэс.
              Цехийн ХАБ нь хянагч тул бусдын тайланг устгахгүй. */}
          {r.can_edit && (
            <Popconfirm
              title={
                <span>
                  {r.report_date} — {r.ashua_name}
                  <br />
                  Энэ тайланг устгах уу?
                </span>
              }
              okText="Устгах"
              okButtonProps={{ danger: true }}
              cancelText="Болих"
              onConfirm={async () => {
                try {
                  await xabAshuaDailyApi.remove(r.id);
                  antMessage.success("Устгалаа");
                  load();
                } catch (e) {
                  antMessage.error(
                    e?.response?.data?.message || "Устгаж чадсангүй",
                  );
                }
              }}
            >
              <Button
                size="small"
                danger
                icon={<i className="fas fa-trash" style={{ fontSize: 11 }} />}
              />
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  return (
    <div
      className="ref-compact"
      style={{ background: PAGE_BG, minHeight: "100%", padding: 16 }}
    >
      {/* ── Толгой ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: BRAND + "1F",
            color: BRAND,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
          }}
        >
          <i className="fas fa-calendar-alt" />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#1F2430" }}>
            Өдөр тутмын тайлан
          </div>
          <div style={{ fontSize: 11.5, color: MUTED }}>
            Анхан шатны удирдах ажилтны ХАБЭА тайлан — 14 асуумж
          </div>
        </div>
        <PageHelp />
        <Select
          value={view}
          onChange={setView}
          style={{ width: 150 }}
          options={[
            { value: "list", label: "Тайлангаар" },
            { value: "month", label: "Сараар (матриц)" },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={load}>
          Шинэчлэх
        </Button>
        <Button
          type="primary"
          icon={<i className="fas fa-pen" style={{ marginRight: 6 }} />}
          onClick={() => {
            setFormDate(moment());
            setFormTn(null);
            setFormOpen(true);
          }}
        >
          Өнөөдрийн тайлан бөглөх
        </Button>
      </div>

      {/* ── Хяналтын хэсэг (зөвхөн тайлангаар харагдацад) ── */}
      {view === "list" && (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <KpiCard
          label="Нийт тайлан"
          value={s.total ?? 0}
          icon="fa-file-alt"
          color={BRAND}
          hint={`${s.ashua_count ?? 0} АШУА`}
        />
        <KpiCard
          label="Илгээсэн"
          value={s.submitted ?? 0}
          icon="fa-check-circle"
          color="#16A34A"
        />
        <KpiCard
          label="Ноорог"
          value={s.draft ?? 0}
          icon="fa-pen"
          color="#CA8A04"
        />
        <KpiCard
          label="Зөрүүтэй"
          value={s.mismatch ?? 0}
          icon="fa-not-equal"
          color="#DC2626"
          hint="авто ↔ гар зөрүү"
        />
      </div>
      )}

      {/* ── Шүүлт (нэг мөрөнд) ── */}
      <div
        style={{
          background: "#fff",
          border: "1px solid " + BORDER,
          borderRadius: 10,
          padding: "8px 10px",
          marginBottom: 10,
          display: "flex",
          gap: 8,
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <div style={{ width: 230 }}>
          <div style={filterLbl}>Огноо</div>
          <DatePicker.RangePicker
            style={{ width: "100%" }}
            value={range}
            onChange={setRange}
            allowClear={false}
          />
        </div>
        <div style={{ width: 200 }}>
          <div style={filterLbl}>Бүтцийн нэгж</div>
          {/* autoSelectOwn — хуудас нээхэд хэрэглэгчийн ӨӨРИЙН цех
              анхдагчаар сонгогдоно. Эрхээс хамаарна:
                · цехийн ХАБ / АШУА → зөвхөн өөрийн цех харагдана
                · ХАБЭА хэлтэс (бүгдийг харах эрхтэй) → мөн өөрийн цехээр
                  эхэлнэ, гэхдээ «Бүгд» болгож өөрчилж болно.
              Ингэснээр хуудас нээмэгц утгагүй үйлдвэрийн нийт дүн биш,
              өөрт хамаарах дүр зураг шууд гарна. */}
          <DepartmentTseh
            autoSelectOwn
            value={bn || undefined}
            onChange={(c) => setBn(c || "")}
          />
        </div>
        <div style={{ width: 150 }}>
          <div style={filterLbl}>Төлөв</div>
          <Select
            style={{ width: "100%" }}
            allowClear
            placeholder="Бүгд"
            value={status || undefined}
            onChange={(v) => setStatus(v || "")}
            options={[
              { value: "draft", label: "Ноорог" },
              { value: "submitted", label: "Илгээсэн" },
            ]}
          />
        </div>
        <div style={{ width: 200 }}>
          <div style={filterLbl}>Хайх</div>
          <SearchInput
            className="w-full"
            value={q}
            onChange={setQ}
            debounce={250}
            placeholder="АШУА, нэгжээр…"
          />
        </div>
      </div>

      {/* ── Хамрагдалт — цехийн ХАБ «хэн тайлагнаагүй»-г хардаг ── */}
      {view === "list" && (
      <Coverage
        date={range?.[1] || moment()}
        bn={bn}
        onOpen={(it) => {
          setFormDate(moment(range?.[1] || moment()));
          setFormTn(it.tn);
          setFormOpen(true);
        }}
      />
      )}

      {view === "month" ? (
        <MonthlyView range={range} bn={bn} tn={""} />
      ) : loading ? (
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : !items.length ? (
        <Card bodyStyle={{ padding: 40 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Энэ хугацаанд тайлан бүртгэгдээгүй"
          />
        </Card>
      ) : (
        <Table
          size="small"
          bordered
          rowKey="id"
          columns={columns}
          dataSource={items}
          pagination={{ pageSize: 25, showSizeChanger: true, size: "small" }}
        />
      )}

      <DailyForm
        open={formOpen}
        date={formDate}
        tn={formTn}
        onClose={() => setFormOpen(false)}
        onSaved={load}
      />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   ӨДРИЙН МАЯГТ

   ⚠ Ил харуулах зарчим: «Автомат» мөрийг ЗАСАХГҮЙ — тоо нь манай
     бүртгэлээс гарна. АШУА зөвхөн «Гараар» / «Хэсэгчлэн» мөрийг бөглөнө.
   ══════════════════════════════════════════════════════════════════════ */
function DailyForm({ open, date, tn, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [vals, setVals] = useState({});
  const [note, setNote] = useState("");
  // Автомат мөрийг нуух эсэх — анхдагчаар НУУНА (АШУА-д зөвхөн бөглөх зүйл)
  const [showAuto, setShowAuto] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  // Enter дарахад дараагийн талбар руу шилжинэ. Талбар бүрийн ref-ийг
  // дарааллаар нь хадгална — АШУА талбар дээр хулгана хэрэглэхгүй,
  // гараас нь салалгүй хурдан бөглөнө.
  const inputRefs = useRef([]);

  const load = useCallback(() => {
    if (!open || !date) return;
    setLoading(true);
    xabAshuaDailyApi
      .form({ date: date.format("YYYY-MM-DD"), tn: tn || undefined })
      .then((r) => {
        setForm(r);
        setNote(r?.report?.note || "");
        const v = {};
        (r?.rows || []).forEach((row) => {
          if (row.manual_value !== null && row.manual_value !== undefined) {
            v[row.question_no] = row.manual_value;
          }
        });
        setVals(v);
      })
      .catch((e) =>
        antMessage.error(e?.response?.data?.message || "Маягт нээж чадсангүй"),
      )
      .finally(() => setLoading(false));
  }, [open, date, tn]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (thenSubmit) => {
    setSaving(true);
    try {
      await xabAshuaDailyApi.save({
        date: date.format("YYYY-MM-DD"),
        tn: tn || undefined,
        note,
        values: vals,
      });
      if (thenSubmit && form?.report?.id) {
        const r = await xabAshuaDailyApi.submit(form.report.id);
        if (r?.empty_manual?.length) {
          antMessage.warning(
            "Илгээлээ. Гараар бөглөх №" +
              r.empty_manual.join(", №") +
              " хоосон үлдсэн.",
          );
        } else {
          antMessage.success("Илгээлээ");
        }
      } else {
        antMessage.success("Хадгаллаа");
      }
      onSaved?.();
      load();
      if (thenSubmit) onClose?.();
    } catch (e) {
      antMessage.error(e?.response?.data?.message || "Хадгалж чадсангүй");
    } finally {
      setSaving(false);
    }
  };

  const rows = form?.rows || [];
  const manualRows = rows.filter((r) => r.editable);
  const autoRows = rows.filter((r) => !r.editable);
  const editable = !!form?.editable;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={860}
      style={{ top: 20 }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i className="fas fa-calendar-alt" style={{ color: BRAND }} />
          <span>Өдөр тутмын тайлан — {date?.format("YYYY-MM-DD")}</span>
          {form?.report?.status === "submitted" && (
            <Tag color="green" style={{ marginRight: 0 }}>
              Илгээсэн
            </Tag>
          )}
        </div>
      }
      footer={
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Хаах</Button>
          {/* Хэвлэх нь ноорог, илгээсэн аль ч төлөвт боломжтой */}
          <Button disabled={!form} onClick={() => setPrintOpen(true)}>
            <i className="fas fa-print" style={{ marginRight: 6 }} />
            Хэвлэх
          </Button>
          {/* Илгээсэн тайланг эзэн нь ноорог болгож засах боломжтой.
              Аудитын мөр хэвээр — зөвхөн төлөв буцна. */}
          {!editable &&
            form?.report?.status === "submitted" &&
            (form?.is_owner || form?.report?.can_edit) && (
              <Popconfirm
                title="Ноорог болгож буцаах уу? Дараа нь дахин илгээнэ."
                okText="Буцаах"
                cancelText="Болих"
                onConfirm={async () => {
                  try {
                    await xabAshuaDailyApi.withdraw(form.report.id);
                    antMessage.success("Ноорог боллоо — одоо засна");
                    load();
                    onSaved?.();
                  } catch (e) {
                    antMessage.error(
                      e?.response?.data?.message || "Буцааж чадсангүй",
                    );
                  }
                }}
              >
                <Button>
                  <i className="fas fa-undo" style={{ marginRight: 6 }} />
                  Ноорог болгох
                </Button>
              </Popconfirm>
            )}
          {editable && (
            <>
              <Button loading={saving} onClick={() => save(false)}>
                Хадгалах
              </Button>
              <Button type="primary" loading={saving} onClick={() => save(true)}>
                Хадгалаад илгээх
              </Button>
            </>
          )}
        </div>
      }
    >
      {loading || !form ? (
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin />
        </div>
      ) : (
        <>
          {!editable && form.readonly_reason && (
            <Alert
              type={form.exists ? "info" : "warning"}
              showIcon
              style={{ marginBottom: 10 }}
              message={form.readonly_reason}
              description={
                form.exists
                  ? "Зөвхөн харах горим. Тоог тайлан гаргагч өөрөө бөглөнө."
                  : "Энэ ажилтан тухайн өдөр тайлангаа хараахан бөглөөгүй байна. Доорх тоо нь манай бүртгэлээс тооцсон УРЬДЧИЛСАН дүн."
              }
            />
          )}

          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 10 }}
            message={`${autoRows.length} үзүүлэлт манай бүртгэлээс автоматаар бөглөгдсөн`}
            description={
              editable ? (
                <span>
                  Та зөвхөн доорх <b>{manualRows.length}</b> үзүүлэлтийг л
                  бөглөнө. Автоматаар гарсан тоог харахыг хүсвэл доор дарна уу.
                </span>
              ) : (
                <span>
                  Гараар бөглөх <b>{manualRows.length}</b> үзүүлэлт байна.
                </span>
              )
            }
          />

          {/* ── ГАРААР ОРУУЛАХ (АШУА-д ил) ── */}
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
            {editable ? "Гараар оруулах" : "Гараар оруулах үзүүлэлт"}
          </div>
          {manualRows.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Гараар оруулах үзүүлэлт алга"
            />
          ) : (
            <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
              {manualRows.map((r, idx) => {
                const m = MODE[r.auto_mode] || MODE.none;
                return (
                  <div
                    key={r.question_no}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      border: "1px solid " + BORDER,
                      borderLeft: "3px solid " + m.color,
                      borderRadius: 8,
                      padding: "7px 10px",
                    }}
                  >
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 11,
                        background: m.color + "1A",
                        color: m.color,
                        fontSize: 11,
                        fontWeight: 800,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginTop: 2,
                      }}
                    >
                      {r.question_no}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5 }}>{r.question_text}</div>
                      {r.count_hint && (
                        <div style={{ fontSize: 11, color: MUTED, marginTop: 1 }}>
                          ({r.count_hint})
                        </div>
                      )}
                      {r.auto_mode === "partial" &&
                        r.auto_value !== null &&
                        r.auto_value !== undefined && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#CA8A04",
                              marginTop: 3,
                            }}
                          >
                            <i className="fas fa-adjust" /> Манай бүртгэлээс{" "}
                            <b>{r.auto_value}</b> гарч байна — гүйцээж засна уу.
                            {r.auto_note ? ` ${r.auto_note}` : ""}
                          </div>
                        )}
                    </div>
                    <InputNumber
                      ref={(el) => {
                        inputRefs.current[idx] = el;
                      }}
                      min={0}
                      disabled={!editable}
                      style={{ width: 96 }}
                      value={vals[r.question_no]}
                      placeholder={
                        r.auto_value !== null && r.auto_value !== undefined
                          ? String(r.auto_value)
                          : "Тоо"
                      }
                      onChange={(v) =>
                        setVals((p) => ({ ...p, [r.question_no]: v }))
                      }
                      onPressEnter={() => {
                        // Дараагийн талбар руу. Сүүлийнх дээр Enter дарвал
                        // фокусыг гаргаж утгыг баталгаажуулна (хадгалахгүй —
                        // санамсаргүй илгээхээс сэргийлнэ).
                        const next = inputRefs.current[idx + 1];
                        if (next && next.focus) next.focus();
                        else if (inputRefs.current[idx]?.blur) {
                          inputRefs.current[idx].blur();
                        }
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* ── АВТОМАТ (нугалж харах) ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 6,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700 }}>
              Манай бүртгэлээс автоматаар
            </span>
            <Button size="small" type="link" onClick={() => setShowAuto((v) => !v)}>
              {showAuto ? "нуух" : `харах (${autoRows.length})`}
            </Button>
          </div>
          {showAuto && (
            <div style={{ display: "grid", gap: 4, marginBottom: 12 }}>
              {autoRows.map((r) => (
                <div
                  key={r.question_no}
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    border: "1px solid " + BORDER,
                    borderLeft: "3px solid #16A34A",
                    borderRadius: 8,
                    padding: "5px 10px",
                    background: "#F8FAFC",
                  }}
                >
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      background: "#16A34A1A",
                      color: "#16A34A",
                      fontSize: 10.5,
                      fontWeight: 800,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {r.question_no}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12 }}>{r.question_text}</div>
                    {r.auto_note && (
                      <div style={{ fontSize: 10.5, color: MUTED }}>
                        {r.auto_note}
                      </div>
                    )}
                  </div>
                  <b style={{ fontSize: 15, color: "#16A34A" }}>
                    {r.final_value ?? 0}
                  </b>
                </div>
              ))}
            </div>
          )}

          <div>
            <div style={{ fontSize: 11.5, color: MUTED, marginBottom: 3 }}>
              Тайлбар (заавал биш)
            </div>
            <Input.TextArea
              rows={2}
              disabled={!editable}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {!editable && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 10 }}
              message="Илгээсэн тайлан — засах боломжгүй"
              description="Илгээсний дараа автомат тоо дахин тооцогдохгүй, түүхэн баримт хөлдөнө."
            />
          )}

          <PrintView
            open={printOpen}
            onClose={() => setPrintOpen(false)}
            form={form}
            date={date}
          />
        </>
      )}
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   ХАМРАГДАЛТ — «хэн тайлагнасан, хэн үгүй»

   ЦЕХИЙН ХАБ мэргэжилтэнд зориулав. Тэр өөрөө тайлан бөглөхгүй, харин
   хариуцсан цехийнхээ бүх АШУА тайлагнасан эсэхийг хянана. Зөвхөн ирсэн
   тайланг харуулбал «хэн бөглөөгүй» гэдэг хэзээ ч мэдэгдэхгүй.

   ⚠ АШУА-гийн албан бүртгэл системд БАЙХГҮЙ тул хүлээгдэж буй жагсаалтыг
     үйл ажиллагаанаас (ӨЭА ажилтан бүртгэсэн / наряд өгсөн) дүгнэнэ.
   ══════════════════════════════════════════════════════════════════════ */
function Coverage({ date, bn, onOpen }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], summary: {} });
  const [onlyMissing, setOnlyMissing] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    xabAshuaDailyApi
      .coverage({
        date: date?.format("YYYY-MM-DD"),
        bn_code: bn || undefined,
      })
      .then((r) => setData(r || { items: [], summary: {} }))
      .catch(() => setData({ items: [], summary: {} }))
      .finally(() => setLoading(false));
  }, [date, bn]);

  useEffect(() => {
    load();
  }, [load]);

  const s = data.summary || {};
  const items = useMemo(
    () =>
      onlyMissing
        ? (data.items || []).filter((x) => x.status === "missing")
        : data.items || [],
    [data.items, onlyMissing],
  );

  const pct = s.pct ?? 0;
  const barColor = pct >= 90 ? "#16A34A" : pct >= 60 ? "#CA8A04" : "#DC2626";

  return (
    <Card
      size="small"
      style={{ marginBottom: 10 }}
      bodyStyle={{ padding: "8px 10px" }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i className="fas fa-user-check" style={{ color: BRAND }} />
          <span style={{ fontSize: 12.5 }}>
            Тайлагналтын хамрагдалт — {date?.format("YYYY-MM-DD")}
          </span>
        </div>
      }
      extra={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Select
            size="small"
            style={{ width: 150 }}
            value={onlyMissing ? "missing" : "all"}
            onChange={(v) => setOnlyMissing(v === "missing")}
            options={[
              { value: "missing", label: "Тайлагнаагүй" },
              { value: "all", label: "Бүх АШУА" },
            ]}
          />
          <Button size="small" icon={<ReloadOutlined />} onClick={load} />
        </div>
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 16 }}>
          <Spin size="small" />
        </div>
      ) : (
        <>
          {/* Явцын тууз */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 8,
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  height: 8,
                  background: "#EEF1F5",
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${pct}%`,
                    height: "100%",
                    background: barColor,
                    borderRadius: 4,
                  }}
                />
              </div>
            </div>
            <b style={{ fontSize: 15, color: barColor, minWidth: 44 }}>{pct}%</b>
            <span style={{ fontSize: 11.5, color: MUTED }}>
              {s.reported ?? 0}/{s.expected ?? 0} тайлагнасан
              {s.missing ? (
                <>
                  {" · "}
                  <b style={{ color: "#DC2626" }}>{s.missing} дутуу</b>
                </>
              ) : null}
            </span>
          </div>

          {items.length === 0 ? (
            <div
              style={{
                fontSize: 12,
                color: onlyMissing ? "#16A34A" : MUTED,
                padding: "4px 0",
              }}
            >
              {onlyMissing ? (
                <>
                  <i className="fas fa-check-circle" /> Бүх АШУА тайлагнасан
                  байна.
                </>
              ) : (
                "АШУА илэрсэнгүй"
              )}
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
                gap: 6,
              }}
            >
              {items.map((it) => {
                const missing = it.status === "missing";
                const c = missing
                  ? "#DC2626"
                  : it.status === "submitted"
                    ? "#16A34A"
                    : "#CA8A04";
                return (
                  <div
                    key={it.tn}
                    onClick={() => !missing && onOpen?.(it)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      border: "1px solid " + BORDER,
                      borderLeft: "3px solid " + c,
                      borderRadius: 8,
                      padding: "5px 8px",
                      cursor: missing ? "default" : "pointer",
                    }}
                  >
                    <span
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 12,
                        background: c + "1A",
                        color: c,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        flexShrink: 0,
                      }}
                    >
                      <i
                        className={
                          "fas " +
                          (missing
                            ? "fa-times"
                            : it.status === "submitted"
                              ? "fa-check"
                              : "fa-pen")
                        }
                      />
                    </span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {it.name || it.tn}
                      </div>
                      <div style={{ fontSize: 10.5, color: MUTED }}>
                        {it.section_name || it.dept_name || it.dept_code || "—"}
                      </div>
                    </div>
                    <Tooltip
                      title={`Сүүлийн 30 хоногт ${it.activity_count} удаа ӨЭА/наряд бүртгэсэн`}
                    >
                      <span style={{ fontSize: 10.5, color: MUTED }}>
                        {it.activity_count}
                      </span>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ fontSize: 10.5, color: MUTED, marginTop: 8 }}>
            ⚠ АШУА-гийн албан бүртгэл системд байхгүй тул энэ жагсаалтыг
            сүүлийн 30 хоногт ӨЭА ажилтан бүртгэсэн эсвэл наряд өгсөн хүнээр
            дүгнэв — ойролцоолол.
          </div>
        </>
      )}
    </Card>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   ХЭВЛЭХ ХУУДАС

   Эх системийн маягттай ижил бүтэц: толгойд огноо/АШУА/нэгж, дараа нь
   Д/Д · АСУУЛТ · АЖИЛТНЫ ТОО гэсэн 3 баганат хүснэгт.

   ⚠ Хэвлэхэд `final_value` буюу ЭЦСИЙН тоог гаргана — авто эсвэл гар аль
     нь ч бай. Цаасан дээр «энэ тоо хаанаас гарсан» гэдэг хамаагүй,
     харин дэлгэц дээр ялгаж харуулна.

   Төслийн хэвлэх жишиг: high_risk/print/index.js — `@media print` дотор
   бүх чимэглэлийг арилгаж, зөвхөн маягтыг үлдээнэ.
   ══════════════════════════════════════════════════════════════════════ */
function PrintView({ open, onClose, form, date }) {
  if (!open || !form) return null;
  const rep = form.report || {};
  const rows = form.rows || [];
  const total = rows.reduce((a, r) => a + (r.final_value || 0), 0);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={880}
      style={{ top: 12 }}
      title="Хэвлэх"
      footer={
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Хаах</Button>
          <Button type="primary" onClick={() => window.print()}>
            <i className="fas fa-print" style={{ marginRight: 6 }} />
            Хэвлэх
          </Button>
        </div>
      }
    >
      <style>{`
        .ad-print { background:#fff; color:#000; font-size:12px; }
        .ad-print h3 { text-align:center; font-size:14px; margin:0 0 2px; }
        .ad-print .sub { text-align:center; font-size:11px; color:#444;
                         margin-bottom:10px; }
        .ad-print .meta { display:flex; flex-wrap:wrap; gap:4px 24px;
                          font-size:11px; margin-bottom:8px; }
        .ad-print table { width:100%; border-collapse:collapse; }
        .ad-print th, .ad-print td { border:1px solid #000; padding:4px 6px;
                                     vertical-align:top; }
        .ad-print th { background:#EEE; font-size:11px; text-align:center; }
        .ad-print td.no { text-align:center; width:34px; }
        .ad-print td.val { text-align:center; width:86px; font-weight:700; }
        .ad-print .hint { font-size:10px; color:#555; }
        .ad-print .sign { margin-top:16px; display:flex; gap:40px;
                          font-size:11px; }

        @media print {
          /* Модалын хүрээ, товч, дэлгэцийн бусад бүх зүйлийг нуух —
             цаасан дээр зөвхөн маягт үлдэнэ. */
          body * { visibility:hidden !important; }
          .ad-print, .ad-print * { visibility:visible !important; }
          .ad-print { position:absolute; left:0; top:0; width:100%; }
          .ant-modal-mask, .ant-modal-header, .ant-modal-footer,
          .ant-modal-close { display:none !important; }
          .ant-modal { top:0 !important; padding:0 !important; max-width:100% !important; }
          .ant-modal-content { box-shadow:none !important; }
          @page { size:A4 portrait; margin:10mm; }
        }
      `}</style>

      <div className="ad-print">
        <h3>АНХАН ШАТНЫ УДИРДАХ АЖИЛТНЫ ХАБЭА ӨДӨР ТУТМЫН ТАЙЛАН</h3>
        <div className="sub">{date?.format("YYYY оны MM сарын DD өдөр")}</div>

        <div className="meta">
          <span>
            <b>Бүртгэсэн ажилтан:</b> {rep.ashua_name || "—"}
            {rep.ashua_tn ? ` (ТН ${rep.ashua_tn})` : ""}
          </span>
          <span>
            <b>Албан тушаал:</b> {rep.ashua_position || "—"}
          </span>
          <span>
            <b>Бүтцийн нэгж:</b> {rep.dept_name || rep.dept_code || "—"}
          </span>
          <span>
            <b>Хэсэг:</b> {rep.section_name || rep.section_code || "—"}
          </span>
          <span>
            <b>Төлөв:</b>{" "}
            {rep.status === "submitted" ? "Илгээсэн" : "Ноорог"}
          </span>
        </div>

        <table>
          <thead>
            <tr>
              <th style={{ width: 34 }}>Д/Д</th>
              <th>АСУУЛТ</th>
              <th style={{ width: 86 }}>АЖИЛТНЫ ТОО</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.question_no}>
                <td className="no">{r.question_no}</td>
                <td>
                  {r.question_text}
                  {r.count_hint ? (
                    <div className="hint">({r.count_hint})</div>
                  ) : null}
                </td>
                <td className="val">
                  {r.final_value === null || r.final_value === undefined
                    ? ""
                    : r.final_value}
                </td>
              </tr>
            ))}
            <tr>
              <td className="no" />
              <td style={{ textAlign: "right", fontWeight: 700 }}>Нийт</td>
              <td className="val">{total}</td>
            </tr>
          </tbody>
        </table>

        {rep.note ? (
          <div style={{ marginTop: 8, fontSize: 11 }}>
            <b>Тайлбар:</b> {rep.note}
          </div>
        ) : null}

        <div className="sign">
          <span>Тайлан гаргасан: ..................................</span>
          <span>Хянасан (Цехийн ХАБ): ..................................</span>
        </div>
      </div>
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   САРЫН ХАРАГДАЦ — эх системийн 2-р дэлгэцтэй ижил матриц

       мөр  = өдөр (+ АШУА)
       багана = 14 асуумж
       нүд  = тоо

   Эх системд АШУА бүр өөрийн мөрөө хардаг ба цехийн ХАБ бүх хүнийг нэг
   дор харах шаардлагатай. Тиймээс `tn` заагаагүй бол хамрах хүрээн доторх
   БҮХ АШУА гарна.

   ⚠ 14 багана нь нарийн тул толгойд зөвхөн ДУГААР харуулж, бүтэн асуултыг
     tooltip-д өгнө. Эс бөгөөс хүснэгт хэвтээ чиглэлд уншигдахгүй болно.
   ══════════════════════════════════════════════════════════════════════ */
function MonthlyView({ range, bn, tn }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [printOpen, setPrintOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    xabAshuaDailyApi
      .matrix({
        from: range?.[0]?.format("YYYY-MM-DD"),
        to: range?.[1]?.format("YYYY-MM-DD"),
        bn_code: bn || undefined,
        tn: tn || undefined,
      })
      .then(setData)
      .catch((e) =>
        antMessage.error(e?.response?.data?.message || "Уншиж чадсангүй"),
      )
      .finally(() => setLoading(false));
  }, [range, bn, tn]);

  useEffect(() => {
    load();
  }, [load]);

  // useMemo-д ороож тогтворжуулна — эс бөгөөс рендер бүрд шинэ массив
  // үүсч доорх columns useMemo байнга дахин тооцогдоно.
  const qs = useMemo(() => data?.questions || [], [data]);
  const rows = useMemo(() => data?.rows || [], [data]);

  const columns = useMemo(() => {
    const base = [
      {
        title: "Огноо",
        dataIndex: "report_date",
        width: 92,
        fixed: "left",
        render: (v) => <b style={{ fontSize: 11.5 }}>{v}</b>,
      },
      {
        title: "АШУА",
        dataIndex: "ashua_name",
        width: 130,
        fixed: "left",
        render: (v, r) => (
          <div style={{ fontSize: 11.5 }}>
            {v || r.ashua_tn}
            {r.status === "submitted" ? (
              <i
                className="fas fa-check-circle"
                style={{ color: "#16A34A", marginLeft: 4, fontSize: 10 }}
              />
            ) : null}
          </div>
        ),
      },
    ];
    qs.forEach((q) => {
      base.push({
        title: (
          <Tooltip
            title={
              <span>
                <b>№{q.question_no}.</b> {q.question_text}
                {q.count_hint ? ` (${q.count_hint})` : ""}
              </span>
            }
          >
            <span style={{ fontSize: 11 }}>{q.question_no}</span>
          </Tooltip>
        ),
        dataIndex: ["cells", String(q.question_no)],
        width: 46,
        align: "center",
        render: (c) => {
          if (!c || c.v === null || c.v === undefined) {
            return <span style={{ color: "#D0D5DD" }}>—</span>;
          }
          return (
            <span
              style={{
                fontSize: 11.5,
                fontWeight: c.v ? 700 : 400,
                // Гараар бичсэнийг ялгана — авто нь ногоон, гар нь хар
                color: c.v ? (c.m ? "#1F2430" : "#16A34A") : MUTED,
              }}
            >
              {c.v}
            </span>
          );
        },
      });
    });
    base.push({
      title: "Нийт",
      dataIndex: "total",
      width: 62,
      align: "right",
      fixed: "right",
      render: (v) => <b style={{ fontSize: 11.5 }}>{v}</b>,
    });
    return base;
  }, [qs]);

  const s = data?.summary || {};

  return (
    <>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <KpiCard
          label="Тайлант өдөр"
          value={s.days ?? 0}
          icon="fa-calendar-check"
          color={BRAND}
          hint={`${s.reports ?? 0} тайлан`}
        />
        <KpiCard
          label="АШУА"
          value={s.ashua ?? 0}
          icon="fa-user-tie"
          color="#0EA5E9"
        />
        <KpiCard
          label="Илгээсэн"
          value={s.submitted ?? 0}
          icon="fa-check-circle"
          color="#16A34A"
        />
        <KpiCard
          label="Нийт дүн"
          value={data?.grand_total ?? 0}
          icon="fa-calculator"
          color="#7C3AED"
        />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 11.5, color: MUTED }}>
          Багананы дугаар дээр хулгана аваачвал бүтэн асуулт харагдана.
          <span style={{ color: "#16A34A", marginLeft: 8 }}>■</span> автомат
          <span style={{ color: "#1F2430", marginLeft: 8 }}>■</span> гараар
        </span>
        <div style={{ flex: 1 }} />
        <Button size="small" icon={<ReloadOutlined />} onClick={load}>
          Шинэчлэх
        </Button>
        <Button
          size="small"
          type="primary"
          disabled={!rows.length}
          onClick={() => setPrintOpen(true)}
        >
          <i className="fas fa-print" style={{ marginRight: 6 }} />
          Хэвлэх
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : !rows.length ? (
        <Card bodyStyle={{ padding: 40 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Энэ хугацаанд тайлан алга"
          />
        </Card>
      ) : (
        <Table
          size="small"
          bordered
          rowKey="id"
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ x: 1100, y: 520 }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ background: "#F8FAFC" }}>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <b style={{ fontSize: 11.5 }}>Сарын дүн</b>
                </Table.Summary.Cell>
                {qs.map((q, i) => (
                  <Table.Summary.Cell
                    key={q.question_no}
                    index={2 + i}
                    align="center"
                  >
                    <b style={{ fontSize: 11.5 }}>
                      {data?.totals?.[String(q.question_no)] ?? 0}
                    </b>
                  </Table.Summary.Cell>
                ))}
                <Table.Summary.Cell index={2 + qs.length} align="right">
                  <b style={{ fontSize: 11.5 }}>{data?.grand_total ?? 0}</b>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      )}

      <MonthlyPrint
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        data={data}
        range={range}
      />
    </>
  );
}

/** Сарын матрицын хэвлэх хуудас — A4 хэвтээ */
function MonthlyPrint({ open, onClose, data, range }) {
  if (!open || !data) return null;
  const qs = data.questions || [];
  const rows = data.rows || [];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1100}
      style={{ top: 12 }}
      title="Сарын тайлан хэвлэх"
      footer={
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Хаах</Button>
          <Button type="primary" onClick={() => window.print()}>
            <i className="fas fa-print" style={{ marginRight: 6 }} />
            Хэвлэх
          </Button>
        </div>
      }
    >
      <style>{`
        .adm-print { background:#fff; color:#000; font-size:10px; }
        .adm-print h3 { text-align:center; font-size:13px; margin:0 0 2px; }
        .adm-print .sub { text-align:center; font-size:10px; color:#444;
                          margin-bottom:8px; }
        .adm-print table { width:100%; border-collapse:collapse; }
        .adm-print th, .adm-print td { border:1px solid #000; padding:2px 3px; }
        .adm-print th { background:#EEE; font-size:9px; text-align:center; }
        .adm-print td.c { text-align:center; }
        .adm-print tr.sum td { background:#F0F0F0; font-weight:700; }
        .adm-print .legend { font-size:9px; color:#555; margin-top:6px; }

        @media print {
          body * { visibility:hidden !important; }
          .adm-print, .adm-print * { visibility:visible !important; }
          .adm-print { position:absolute; left:0; top:0; width:100%; }
          .ant-modal-mask, .ant-modal-header, .ant-modal-footer,
          .ant-modal-close { display:none !important; }
          .ant-modal { top:0 !important; padding:0 !important;
                       max-width:100% !important; }
          .ant-modal-content { box-shadow:none !important; }
          @page { size:A4 landscape; margin:8mm; }
        }
      `}</style>

      <div className="adm-print">
        <h3>АНХАН ШАТНЫ УДИРДАХ АЖИЛТНЫ ХАБЭА ӨДӨР ТУТМЫН ТАЙЛАН</h3>
        <div className="sub">
          {range?.[0]?.format("YYYY-MM-DD")} — {range?.[1]?.format("YYYY-MM-DD")}
          {rows[0]?.dept_name ? ` · ${rows[0].dept_name}` : ""}
        </div>

        <table>
          <thead>
            <tr>
              <th style={{ width: 62 }}>Огноо</th>
              <th style={{ width: 90 }}>АШУА</th>
              {qs.map((q) => (
                <th key={q.question_no} style={{ width: 26 }}>
                  {q.question_no}
                </th>
              ))}
              <th style={{ width: 34 }}>Нийт</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="c">{r.report_date}</td>
                <td>{r.ashua_name || r.ashua_tn}</td>
                {qs.map((q) => {
                  const c = r.cells?.[String(q.question_no)];
                  return (
                    <td key={q.question_no} className="c">
                      {c && c.v !== null && c.v !== undefined ? c.v : ""}
                    </td>
                  );
                })}
                <td className="c">
                  <b>{r.total}</b>
                </td>
              </tr>
            ))}
            <tr className="sum">
              <td className="c" colSpan={2}>
                Сарын дүн
              </td>
              {qs.map((q) => (
                <td key={q.question_no} className="c">
                  {data.totals?.[String(q.question_no)] ?? 0}
                </td>
              ))}
              <td className="c">{data.grand_total ?? 0}</td>
            </tr>
          </tbody>
        </table>

        <div className="legend">
          Багананы дугаар — асуумжийн Д/Д:{" "}
          {qs
            .map((q) => `${q.question_no}. ${q.question_text}`)
            .join(" · ")}
        </div>
      </div>
    </Modal>
  );
}
